import { useEffect, useRef } from "react";

// Refresh time-based access through Core. Dates only schedule a read; the
// client never derives permissions, grace lengths, or launch policy.
export default function useTrialStatusRefresh(billing, onRefreshBilling, options = {}) {
  const expiryRefreshed = useRef(null);
  const lastTarget = useRef(null);
  const clubId = billing?.clubId;
  const fullAccess = billing?.billingAccessMode === "full";
  const deadlines = [
    fullAccess && billing?.status === "trialing" && billing?.trialEnd,
    fullAccess && billing?.freeAccessUntil,
    fullAccess && billing?.cancelAtPeriodEnd && billing?.currentPeriodEnd,
  ].filter(Boolean).map(Date.parse).filter(Number.isFinite);
  const deadline = deadlines.length ? Math.min(...deadlines) : null;
  // Core does not expose the grace or legacy read-only end as a timestamp.
  // Re-read those states every minute instead of duplicating server policy.
  const poll = ["full", "read_only"].includes(billing?.billingAccessMode) && (
    ["past_due", "unpaid"].includes(billing?.status) ||
    (["not_started", "started_no_subscription"].includes(billing?.billingState) && deadline === null)
  );
  const hasExplicitScope = options.scopeKey !== undefined;
  const scopeKey = hasExplicitScope ? options.scopeKey : clubId;
  const retry = options.retry === true;
  const loading = options.loading === true;
  const hasBilling = billing != null;
  const enabled = billing?.billingEnabled === true && (deadline !== null || poll);

  useEffect(() => {
    if (!scopeKey || lastTarget.current?.scopeKey !== scopeKey) {
      lastTarget.current = null;
      if (hasExplicitScope) expiryRefreshed.current = null;
    }
    if (enabled && clubId) {
      lastTarget.current = { scopeKey, clubId, deadline, poll };
    } else if (hasBilling || (!retry && !loading)) {
      lastTarget.current = null;
    }
    // Retain only the refresh target after a transient request failure. The
    // caller still renders solely from its current validated billing state.
    const target = lastTarget.current;
    if (!scopeKey || !target || !onRefreshBilling || loading) return undefined;
    const end = target.deadline;
    const key = `${scopeKey}:${target.clubId}:${target.deadline}`;
    const tick = () => {
      if (end !== null && Date.now() >= end) {
        // A successful read may still contain the old projection (or the browser
        // clock may be ahead). Retry until Core removes or advances the target.
        // Keep the cadence across effect/StrictMode restarts to avoid a loop.
        const previous = expiryRefreshed.current;
        if (previous?.key !== key || Date.now() - previous.at >= 60000) {
          expiryRefreshed.current = { key, at: Date.now() };
          onRefreshBilling(String(target.clubId));
        }
        return true;
      }
      return false;
    };
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      // A reconnect/focus event may retry a failed request immediately. Keep the
      // one-minute cadence for automatic retries of an unchanged valid response.
      if (retry) {
        expiryRefreshed.current = { key, at: Date.now() };
        onRefreshBilling(String(target.clubId));
      } else if (!tick()) onRefreshBilling(String(target.clubId));
    };
    tick();
    const timer = setInterval(() => {
      if (!tick() && target.poll) onRefreshBilling(String(target.clubId));
    }, 60000);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [clubId, enabled, hasBilling, hasExplicitScope, onRefreshBilling, retry, loading, scopeKey, deadline, poll]);
}
